import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../../services/api';
import { Megaphone, Bell, Trophy, X } from 'lucide-react';

/* ===================== ANNOUNCEMENTS ===================== */
export function AnnouncementsPanel({ close }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/announcements');
        setItems(res.data?.announcements || res.data?.items || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="h-full flex flex-col" data-testid="announcements-panel">
      <div className="px-5 py-4 border-b flex items-center gap-3" style={{ borderColor: 'rgba(255,217,61,0.25)' }}>
        <span className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: 'linear-gradient(180deg, #FFD93D, #F4B400)', boxShadow: '0 4px 14px rgba(244,180,0,0.45)' }}>
          <Megaphone className="w-5 h-5" style={{ color: '#3F2A00' }} strokeWidth={2.5} />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="display font-extrabold text-lg" style={{ color: '#fff' }}>Announcements</h2>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>News & updates from the team</p>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading && <div className="py-16 text-center text-white/60">Loading...</div>}
        {!loading && items.length === 0 && (
          <div className="py-16 text-center">
            <Megaphone className="w-10 h-10 mx-auto mb-3 text-amber-300 opacity-60" />
            <p className="display font-bold text-white/80">No announcements yet</p>
            <p className="text-sm text-white/50 mt-1">Check back later for news and promotions.</p>
          </div>
        )}
        {items.map((a) => (
          <div key={a.id} className="rounded-xl p-4"
            style={{ background: 'rgba(255,217,61,0.08)', border: '1px solid rgba(255,217,61,0.35)' }}
            data-testid={`announcement-item-${a.id}`}>
            <p className="display font-extrabold text-sm" style={{ color: '#FFD93D' }}>{a.title || 'Update'}</p>
            {a.body && <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.85)' }}>{a.body}</p>}
            {a.created_at && (
              <p className="text-[10px] mt-2 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {new Date(a.created_at).toLocaleString()}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ===================== NOTIFICATIONS ===================== */
export function NotificationsPanel({ close, onRead, onNavigate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get('/api/notifications');
      setItems(res.data?.notifications || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const isUnread = (item) => !item?.read && !item?.is_read;

  const markOneRead = async (notificationId) => {
    try {
      await api.post(`/api/notifications/${notificationId}/read`);
      onRead && onRead();
      setItems(prev => prev.map(item => (
        item.id === notificationId
          ? { ...item, read: true, is_read: true }
          : item
      )));
    } catch {
      toast.error('Failed');
    }
  };

  const markAllRead = async () => {
    try {
      await api.post('/api/notifications/mark-all-read');
      onRead && onRead();
      toast.dismiss();
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="h-full flex flex-col" data-testid="notifications-panel">
      <div className="px-5 pr-20 py-4 border-b flex items-center gap-3" style={{ borderColor: 'rgba(34,255,136,0.25)' }}>
        <span className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: 'linear-gradient(180deg, var(--vl-btn-primary-from, #22FF88), var(--vl-btn-primary-to, #00C853))', boxShadow: '0 4px 14px rgba(34,255,136,0.45)' }}>
          <Bell className="w-5 h-5" style={{ color: 'var(--vl-btn-primary-text, #02140A)' }} strokeWidth={2.5} />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="display font-extrabold text-lg" style={{ color: '#fff' }}>Alerts</h2>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>Deposits, cashouts, wallet updates, and activity</p>
        </div>
        {items.some(isUnread) && (
          <button onClick={markAllRead} className="text-xs display font-bold px-3 py-1.5 rounded-lg"
            style={{ background: 'rgba(34,255,136,0.15)', color: 'var(--vl-neon, #22FF88)', border: '1px solid rgba(34,255,136,0.35)' }}
            data-testid="notif-mark-all-read">
            Mark all read
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {loading && <div className="py-16 text-center text-white/60">Loading...</div>}
        {!loading && items.length === 0 && (
          <div className="py-16 text-center">
            <Bell className="w-10 h-10 mx-auto mb-3 text-emerald-300 opacity-60" />
            <p className="display font-bold text-white/80">You're all caught up</p>
            <p className="text-sm text-white/50 mt-1">New alerts will appear here.</p>
          </div>
        )}
        {items.map((n) => (
          <button
            key={n.id}
            type="button"
            onClick={async () => {
              if (isUnread(n)) {
                await markOneRead(n.id);
              }
              toast.dismiss();
              if (n.action_url && onNavigate) {
                onNavigate(n.action_url);
              }
            }}
            className="w-full rounded-lg px-3 py-2.5 text-left transition active:scale-[0.995]"
            style={{
              background: isUnread(n) ? 'rgba(34,255,136,0.08)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${isUnread(n) ? 'rgba(34,255,136,0.35)' : 'rgba(255,255,255,0.06)'}`,
            }}
            data-testid={`notif-item-${n.id}`}>
            <div className="flex items-start gap-2">
              <span className="mt-1 w-2 h-2 rounded-full shrink-0"
                style={{ background: isUnread(n) ? 'var(--vl-neon, #22FF88)' : 'transparent', boxShadow: isUnread(n) ? '0 0 8px var(--vl-neon, #22FF88)' : 'none' }} />
              <div className="flex-1 min-w-0">
                <p className="display text-sm font-bold" style={{ color: '#fff' }}>{n.title || 'Notification'}</p>
                {n.message && <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.72)' }}>{n.message}</p>}
                {n.created_at && (
                  <p className="text-[10px] mt-1 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    {new Date(n.created_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ===================== ACHIEVEMENTS ===================== */
export function AchievementsPanel({ close }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/achievements');
        setItems(res.data?.achievements || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="h-full flex flex-col" data-testid="achievements-panel">
      <div className="px-5 py-4 border-b flex items-center gap-3" style={{ borderColor: 'rgba(249,115,22,0.25)' }}>
        <span className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: 'linear-gradient(180deg, #F97316, #C2410C)', boxShadow: '0 4px 14px rgba(249,115,22,0.45)' }}>
          <Trophy className="w-5 h-5" style={{ color: '#fff' }} strokeWidth={2.5} />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="display font-extrabold text-lg" style={{ color: '#fff' }}>Achievements</h2>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>Unlock rewards by playing</p>
        </div>
        <button onClick={close} className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-white/5" aria-label="Close">
          <X className="w-4 h-4" style={{ color: '#fff' }} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {loading && <div className="py-16 text-center text-white/60">Loading...</div>}
        {!loading && items.length === 0 && (
          <div className="py-16 text-center">
            <Trophy className="w-10 h-10 mx-auto mb-3 text-orange-300 opacity-60" />
            <p className="display font-bold text-white/80">No achievements yet</p>
            <p className="text-sm text-white/50 mt-1">Play games to start earning badges and rewards.</p>
          </div>
        )}
        {items.map((a) => {
          const progress = Math.min(100, Math.round(((a.progress || 0) / Math.max(1, a.target || 1)) * 100));
          const unlocked = a.unlocked || a.claimed || progress >= 100;
          return (
            <div key={a.id} className="rounded-xl p-3"
              style={{
                background: unlocked ? 'rgba(249,115,22,0.10)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${unlocked ? 'rgba(249,115,22,0.45)' : 'rgba(255,255,255,0.08)'}`,
              }}
              data-testid={`achievement-item-${a.id}`}>
              <div className="flex items-center gap-3">
                <span className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{
                    background: unlocked ? 'linear-gradient(180deg, #F97316, #C2410C)' : 'rgba(255,255,255,0.06)',
                    boxShadow: unlocked ? '0 4px 14px rgba(249,115,22,0.55)' : 'none',
                  }}>
                  <Trophy className="w-5 h-5" style={{ color: unlocked ? '#fff' : 'rgba(255,255,255,0.5)' }} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="display font-bold text-sm" style={{ color: '#fff' }}>{a.title || a.name}</p>
                  {a.description && <p className="text-xs mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.65)' }}>{a.description}</p>}
                </div>
                {a.reward && (
                  <span className="shrink-0 display font-extrabold text-xs px-2 py-0.5 rounded"
                    style={{ background: unlocked ? '#FFD93D' : 'rgba(255,255,255,0.08)', color: unlocked ? '#3F2A00' : 'rgba(255,255,255,0.6)' }}>
                    {a.reward}
                  </span>
                )}
              </div>
              <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div className="h-full rounded-full" style={{
                  width: `${progress}%`,
                  background: unlocked ? 'linear-gradient(90deg, #F97316, #FFD93D)' : 'linear-gradient(90deg, var(--vl-btn-primary-from, #22FF88), var(--vl-btn-primary-to, #00C853))',
                  transition: 'width 0.4s ease',
                }} />
              </div>
              <p className="text-[10px] mt-1 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {progress}% {unlocked ? '· unlocked' : ''}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
